import { computed, ref, type Ref } from 'vue';

import {
  documentSendApi,
  type DocumentSendFilters,
  type DocumentSendSortField,
} from '@/entities/DocumentSend';
import {
  downloadBlobFile,
  handleApiError,
  useCustomToast,
  type Pagination,
  type SortOrder,
} from '@/shared/lib';

const EXPORT_PROGRESS_TITLE = 'Exportação em andamento';
const EXPORT_PROGRESS_MESSAGE = 'Por favor, aguarde.';
const EXPORT_ERROR_TITLE = 'Erro ao exportar';
const EXPORT_ERROR_RETRY_MESSAGE = 'Erro ao exportar. Tente novamente.';

interface UseDocumentSendControlExportParams {
  searchTerm: Ref<string>;
  sort: Ref<DocumentSendSortField | ''>;
  order: Ref<SortOrder | ''>;
  filters: Ref<DocumentSendFilters>;
  metadata: Ref<Pagination | null>;
  hasLoaded: Ref<boolean>;
}

export function useDocumentSendControlExport({
  searchTerm,
  sort,
  order,
  filters,
  metadata,
  hasLoaded,
}: UseDocumentSendControlExportParams) {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const isExportDisabled = computed(() => {
    if (isExporting.value || !hasLoaded.value) {
      return true;
    }

    return (metadata.value?.total ?? 0) === 0;
  });

  const handleExportToExcel = async (): Promise<void> => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    const progressToastId = toast.showWarningToast(
      {
        title: EXPORT_PROGRESS_TITLE,
        message: EXPORT_PROGRESS_MESSAGE,
        type: 'warning',
      },
      {
        timeout: false,
        closeButton: false,
      }
    );

    try {
      const trimmedSearchTerm = searchTerm.value.trim();
      const { blob, exportedCount, filename } = await documentSendApi.exportDocumentSendsExcel({
        ...filters.value,
        ...(trimmedSearchTerm.length > 0 ? { term: trimmedSearchTerm } : {}),
        ...(sort.value && order.value
          ? {
              sort: sort.value,
              order: order.value,
            }
          : {}),
      });

      downloadBlobFile(blob, filename);

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: exportedCount === 1 ? '1 envio exportado' : `${exportedCount} envios exportados`,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, EXPORT_ERROR_RETRY_MESSAGE);

      toast.showErrorToast({
        title: EXPORT_ERROR_TITLE,
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      toast.dismissToast(progressToastId);
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExportDisabled,
    isExporting,
  };
}
